export type AvatarGroupKey =
  | 'animais' | 'folclore' | 'futebol' | 'carnaval' | 'praia'
  | 'comidas' | 'sorte' | 'pessoas' | 'monstros' | 'festa';

export type AvatarGroup = {
  key: AvatarGroupKey;
  ids: number[];
};

// 10 groups × 10 = 100 default avatars. ID space 1..100 unchanged (matches the
// backend `avatar_default_id` CHECK and the WebP pack in public/avatars/).
const GROUP_KEYS: AvatarGroupKey[] = [
  'animais', 'folclore', 'futebol', 'carnaval', 'praia',
  'comidas', 'sorte', 'pessoas', 'monstros', 'festa',
];

const GROUPS: AvatarGroup[] = GROUP_KEYS.map((key, index) => ({
  key,
  ids: range(index * 10 + 1, index * 10 + 10),
}));

// Primary source: the finished WebP pack, served as static assets. Rendered by
// <img src>, lazily fetched by the browser (only on-screen avatars download).
// Bump ?v= when the pack is re-exported so immutable caches refresh.
const AVATAR_PACK_VERSION = '1';

export function defaultAvatarUrl(id: number): string {
  const safeId = Number.isInteger(id) && id >= 1 && id <= 100 ? id : 1;
  return `/avatars/avatar-${safeId}.webp?v=${AVATAR_PACK_VERSION}`;
}

// Resolve any user-ish object to a renderable avatar URL: an uploaded/custom URL
// wins, else the picked default-pack id → static WebP, else '' (caller shows the
// initial fallback). Works for the auth user and for chat/leaderboard user blobs.
export function resolveAvatarUrl(user: {
  avatar_url?: string; avatarUrl?: string; avatar?: string; avatar_thumb?: string; avatarThumb?: string;
  avatar_default_id?: number; avatarDefaultId?: number;
} | null | undefined): string {
  const direct = user?.avatar_url || user?.avatarUrl || user?.avatar || user?.avatar_thumb || user?.avatarThumb || '';
  if (direct) return direct;
  const id = Number(user?.avatar_default_id ?? user?.avatarDefaultId ?? 0) || 0;
  return id ? defaultAvatarUrl(id) : '';
}

const GRADIENTS: Array<[string, string]> = [
  ['#16a34a', '#facc15'],
  ['#1d4ed8', '#fbbf24'],
  ['#15803d', '#84cc16'],
  ['#0ea5e9', '#22d3ee'],
  ['#f97316', '#fbbf24'],
  ['#7c3aed', '#ec4899'],
  ['#dc2626', '#f97316'],
  ['#059669', '#fbbf24'],
  ['#0891b2', '#0ea5e9'],
  ['#be185d', '#f472b6'],
  ['#0d9488', '#5eead4'],
  ['#a16207', '#fbbf24'],
  ['#9333ea', '#c084fc'],
  ['#1e40af', '#3b82f6'],
  ['#b45309', '#fbbf24'],
  ['#065f46', '#34d399'],
  ['#7e22ce', '#a855f7'],
  ['#0c4a6e', '#0ea5e9'],
  ['#dc2626', '#fde047'],
  ['#1f2937', '#fbbf24'],
  ['#84cc16', '#fde047'],
  ['#0f766e', '#14b8a6'],
  ['#7c2d12', '#f97316'],
  ['#581c87', '#a855f7'],
  ['#831843', '#ec4899'],
];

export function avatarGroups(): AvatarGroup[] {
  return GROUPS;
}

// Fallback only: procedural SVG data URI, used as an <img> onError target when a
// WebP asset fails to load (transition window / cache miss / offline).
export function defaultAvatarSvgDataUri(id: number): string {
  return `data:image/svg+xml;utf8,${encodeURIComponent(defaultAvatarSvg(id))}`;
}

export function defaultAvatarSvg(id: number): string {
  const safeId = Number.isInteger(id) && id >= 1 && id <= 100 ? id : 1;
  const [from, to] = GRADIENTS[(safeId - 1) % GRADIENTS.length];
  const gradientId = `avatar-grad-${safeId}`;
  return [
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">',
    '<defs>',
    `<linearGradient id="${gradientId}" x1="0" y1="0" x2="1" y2="1">`,
    `<stop offset="0%" stop-color="${from}"/>`,
    `<stop offset="100%" stop-color="${to}"/>`,
    '</linearGradient>',
    '</defs>',
    `<rect width="100" height="100" rx="50" fill="url(#${gradientId})"/>`,
    glyphForId(safeId),
    '</svg>',
  ].join('');
}

function glyphForId(id: number): string {
  const slot = (id - 1) % 25;
  const family = Math.floor((id - 1) / 25);
  if (family === 0) {
    const shapes = [
      '<ellipse cx="50" cy="62" rx="24" ry="15" fill="#fff"/><circle cx="50" cy="42" r="15" fill="#fff"/><circle cx="42" cy="35" r="4" fill="#fff"/><circle cx="58" cy="35" r="4" fill="#fff"/>',
      '<circle cx="48" cy="54" r="19" fill="#fff"/><path d="M52 49 L82 39 L72 57 Z" fill="#fff"/><circle cx="43" cy="48" r="2.8" fill="#0f1115"/>',
      '<ellipse cx="45" cy="52" rx="23" ry="14" fill="#fff"/><path d="M68 52 L82 40 L82 64 Z" fill="#fff"/><circle cx="35" cy="48" r="2.8" fill="#0f1115"/>',
      '<path d="M50 50 L25 30 Q21 50 30 65 Q44 60 50 50 Z M50 50 L75 30 Q79 50 70 65 Q56 60 50 50 Z" fill="#fff"/>',
      '<circle cx="50" cy="50" r="23" fill="#fff"/><path d="M30 27 L37 39 L25 39 Z M70 27 L75 39 L63 39 Z" fill="#fff"/><circle cx="42" cy="49" r="3" fill="#0f1115"/><circle cx="58" cy="49" r="3" fill="#0f1115"/>',
    ];
    return shapes[slot % shapes.length];
  }
  if (family === 1) {
    const shapes = [
      '<circle cx="50" cy="50" r="17" fill="#fff"/><g stroke="#fff" stroke-width="4" stroke-linecap="round"><line x1="50" y1="21" x2="50" y2="13"/><line x1="50" y1="87" x2="50" y2="79"/><line x1="21" y1="50" x2="13" y2="50"/><line x1="87" y1="50" x2="79" y2="50"/><line x1="30" y1="30" x2="24" y2="24"/><line x1="76" y1="76" x2="70" y2="70"/></g>',
      '<rect x="46" y="50" width="8" height="33" rx="2" fill="#fff"/><path d="M50 50 Q30 38 18 46 Q34 46 50 50 Z M50 50 Q70 38 82 46 Q66 46 50 50 Z M50 50 Q40 28 26 26 Q40 36 50 50 Z M50 50 Q60 28 74 26 Q60 36 50 50 Z" fill="#fff"/>',
      '<polygon points="50,18 58,42 84,42 63,58 71,82 50,68 29,82 37,58 16,42 42,42" fill="#fff"/>',
      '<path d="M14 56 Q26 38 38 56 Q50 74 62 56 Q74 38 86 56 L86 70 L14 70 Z" fill="#fff"/>',
      '<path d="M22 46 Q30 26 50 30 Q70 26 78 46 Q72 64 50 64 Q28 64 22 46 Z" fill="#fff"/><circle cx="38" cy="46" r="6" fill="#0f1115"/><circle cx="62" cy="46" r="6" fill="#0f1115"/>',
    ];
    return shapes[slot % shapes.length];
  }
  if (family === 2) {
    const skin = ['#fde6c8', '#e8b888', '#b07050', '#7a4a30', '#f3d3a8'][slot % 5];
    const hair = [
      '<path d="M22 40 Q22 18 50 18 Q78 18 78 40 L78 50 Q70 30 50 30 Q30 30 22 50 Z" fill="#fff"/>',
      '<path d="M28 38 Q40 14 50 16 Q60 14 72 38 L72 44 Q66 32 50 32 Q34 32 28 44 Z" fill="#fff"/>',
      '<path d="M22 44 Q24 20 50 20 Q76 20 78 44 L74 44 Q66 32 50 32 Q34 32 26 44 Z M22 44 L18 60 L26 56 Z M78 44 L82 60 L74 56 Z" fill="#fff"/>',
      '<path d="M30 38 Q42 22 50 22 Q58 22 70 38 L70 44 Q60 36 50 36 Q40 36 30 44 Z" fill="#fff"/>',
      '<circle cx="50" cy="32" r="20" fill="#fff"/>',
    ][slot % 5];
    return `<circle cx="50" cy="56" r="22" fill="${skin}"/>${hair}<circle cx="42" cy="54" r="2.2" fill="#0f1115"/><circle cx="58" cy="54" r="2.2" fill="#0f1115"/><path d="M42 64 Q50 70 58 64" stroke="#0f1115" stroke-width="1.8" fill="none" stroke-linecap="round"/>`;
  }
  const jerseyNum = slot + 1;
  return `<circle cx="50" cy="36" r="16" fill="#fff"/><g stroke="#0f1115" stroke-width="1.8" fill="none"><circle cx="50" cy="36" r="16"/><path d="M50 20 L45 30 L55 30 Z M50 52 L45 42 L55 42 Z M34 36 L42 32 L42 40 Z M66 36 L58 32 L58 40 Z"/></g><rect x="22" y="58" width="56" height="30" rx="4" fill="#fff"/><text x="50" y="80" text-anchor="middle" font-size="22" font-weight="800" fill="#0f1115" font-family="ui-sans-serif">${jerseyNum}</text>`;
}

function range(start: number, end: number) {
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}
